import { http } from '../../../../api/http';
import type {
  ParteProduccion,
  ParteFromGuiaInfo,
  ProductoTerminadoTipoB,
  CreateParteRequest,
  UpdateParteRequest,
  ParteProduccionDetailResponse,
  PresentacionTipoB,
  ProductoTipoBGrid,
  ParteResumen
} from '../types';

export class ParteProduccionService {
  private static readonly BASE_URL = '/produccion/Parte/partes-produccion';
  private static readonly PARTE_URL = '/produccion/Parte';

  static async getAll(page: number = 1, pageSize: number = 10): Promise<{ data: ParteProduccion[]; totalCount: number }> {
    try {
      const response = await http.get(`${this.BASE_URL}?page=${page}&pageSize=${pageSize}`);

      // If the API returns an array directly, wrap it in the expected structure
      if (Array.isArray(response.data)) {
        return {
          data: response.data,
          totalCount: response.data.length
        };
      }

      // If the API returns the expected structure, use it
      return response.data;
    } catch (error) {
      console.error('Error fetching partes produccion:', error);
      throw error;
    }
  }

  static async getById(id: number): Promise<ParteProduccionDetailResponse> {
    try {
      const response = await http.get(`${this.BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching parte produccion with id ${id}:`, error);
      throw error;
    }
  }

  static async getParteInfoFromGuia(idGuiaProduccion: number): Promise<ParteFromGuiaInfo> {
    try {
      const response = await http.get(`${this.PARTE_URL}/from-guia/${idGuiaProduccion}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching parte info from guia ${idGuiaProduccion}:`, error);
      throw error;
    }
  }

  static async create(data: CreateParteRequest): Promise<ParteProduccion> {
    try {
      const response = await http.post(this.BASE_URL, data);
      return response.data;
    } catch (error) {
      console.error('Error creating parte produccion:', error);
      throw error;
    }
  }

  static async update(id: number, data: UpdateParteRequest): Promise<ParteProduccion> {
    try {
      const response = await http.put(`${this.BASE_URL}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating parte produccion with id ${id}:`, error);
      throw error;
    }
  }

  static async updateEstado(id: number, estado: string): Promise<void> {
    try {
      await http.patch(`${this.BASE_URL}/${id}/estado`, { estado });
    } catch (error) {
      console.error(`Error updating estado of parte produccion with id ${id}:`, error);
      throw error;
    }
  }

  static async delete(id: number): Promise<void> {
    try {
      await http.delete(`${this.BASE_URL}/${id}`);
    } catch (error) {
      console.error(`Error deleting parte produccion with id ${id}:`, error);
      throw error;
    }
  }

  static async searchProductosTipoB(term?: string): Promise<ProductoTerminadoTipoB[]> {
    try {
      const url = term
        ? `${this.PARTE_URL}/productos-tipo-b?term=${encodeURIComponent(term)}`
        : `${this.PARTE_URL}/productos-tipo-b`;
      const response = await http.get(url);
      return response.data;
    } catch (error) {
      console.error('Error searching productos tipo B:', error);
      throw error;
    }
  }

  static async getPresentacionesTipoB(excludeParteId?: number): Promise<PresentacionTipoB[]> {
    try {
      const params = excludeParteId ? { excludeParteId } : {};
      const response = await http.get(`${this.PARTE_URL}/productos-tipo-b/presentaciones`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching presentaciones tipo B:', error);
      throw error;
    }
  }

  static async getProductosTipoBByPresentacion(idPresentacion: number, excludeParteId?: number): Promise<ProductoTipoBGrid> {
    try {
      const params = excludeParteId ? { excludeParteId } : {};
      const response = await http.get(`${this.PARTE_URL}/productos-tipo-b/presentacion/${idPresentacion}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching productos tipo B for presentacion ${idPresentacion}:`, error);
      throw error;
    }
  }

  static async getPartesResumen(): Promise<ParteResumen[]> {
    try {
      const response = await http.get(`${this.PARTE_URL}/partes-resumen`);
      return response.data;
    } catch (error) {
      console.error('Error fetching partes resumen:', error);
      throw error;
    }
  }
}
